import { create } from 'zustand'
import { createJSONStorage, persist, type StateStorage } from 'zustand/middleware'

import { themeSchema, type ThemeName } from '@/features/settings/schemas/theme'

type UiState = {
  sidebarOpen: boolean
  theme: ThemeName
  setSidebarOpen: (open: boolean) => void
  toggleSidebar: () => void
  setTheme: (theme: ThemeName) => void
  toggleTheme: () => void
}

function readSystemTheme(): ThemeName {
  if (typeof window === 'undefined') {
    return 'light'
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

const memoryStorage = new Map<string, string>()

const safeStorage: StateStorage = {
  getItem: (name) => {
    try {
      if (typeof localStorage?.getItem === 'function') {
        return localStorage.getItem(name)
      }
    } catch {
      // fall through
    }

    return memoryStorage.get(name) ?? null
  },
  setItem: (name, value) => {
    try {
      if (typeof localStorage?.setItem === 'function') {
        localStorage.setItem(name, value)
        return
      }
    } catch {
      // fall through
    }

    memoryStorage.set(name, value)
  },
  removeItem: (name) => {
    try {
      if (typeof localStorage?.removeItem === 'function') {
        localStorage.removeItem(name)
        return
      }
    } catch {
      // fall through
    }

    memoryStorage.delete(name)
  },
}

/** UI-only store — never hold server state (user, reservations, etc.). */
export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      sidebarOpen: false,
      theme: readSystemTheme(),
      setSidebarOpen: (sidebarOpen) => {
        set({ sidebarOpen })
      },
      toggleSidebar: () => {
        set((state) => ({ sidebarOpen: !state.sidebarOpen }))
      },
      setTheme: (theme) => {
        set({ theme: themeSchema.parse(theme) })
      },
      toggleTheme: () => {
        set((state) => ({ theme: state.theme === 'light' ? 'dark' : 'light' }))
      },
    }),
    {
      name: 'eatout-ui',
      storage: createJSONStorage(() => safeStorage),
      partialize: (state) => ({ theme: state.theme }),
    },
  ),
)
